import React from 'react';
import { loadResults, earningsWorkbook, deductionsWorkbook } from '../data/mockData';

const loaded = loadResults.filter(r => r.earningsStatus === 'LOADED').length;
const blocked = loadResults.filter(r => r.earningsStatus === 'BLOCKED').length;
const totalEarRows = earningsWorkbook.filter(r => r.status !== 'CRITICAL').length;
const totalDedRows = deductionsWorkbook.filter(r => r.status !== 'HIGH').length;

function StatusDot({ status }) {
  return (
    <span className={`status-dot ${status.toLowerCase()}`} />
  );
}

export default function ApiLoader() {
  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h2>API Loader Results</h2>
          <p>Step 6 — PUT-based idempotent load to PrimePay API v2.3</p>
        </div>
      </div>

      {/* Summary */}
      <div className="load-summary section-gap">
        <div className="load-stat">
          <div className="ls-num" style={{ color: 'var(--success)' }}>{loaded}</div>
          <div className="ls-label">Employees Loaded</div>
        </div>
        <div className="load-stat">
          <div className="ls-num" style={{ color: 'var(--error)' }}>{blocked}</div>
          <div className="ls-label">Blocked (CRITICAL flags)</div>
        </div>
        <div className="load-stat">
          <div className="ls-num">{totalEarRows}</div>
          <div className="ls-label">Earning Rows Submitted</div>
        </div>
        <div className="load-stat">
          <div className="ls-num">{totalDedRows}</div>
          <div className="ls-label">Deduction Rows Submitted</div>
        </div>
      </div>

      {/* Load table */}
      <div className="card section-gap">
        <div className="card-header">
          <h2>Per-Employee Load Status</h2>
          <span className="card-count">{loadResults.length} employees</span>
        </div>
        <div className="data-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>GUID</th>
                <th className="center">Earnings Rows</th>
                <th>Earnings Status</th>
                <th className="center">Deduction Rows</th>
                <th>Deductions Status</th>
                <th>HTTP</th>
                <th>Timestamp (UTC)</th>
              </tr>
            </thead>
            <tbody>
              {loadResults.map(r => {
                const isBlocked = r.earningsStatus === 'BLOCKED';
                return (
                  <tr key={r.employeeUniqueId} style={{ background: isBlocked ? '#fff8f8' : undefined }}>
                    <td style={{ fontWeight: 600 }}>{r.name}</td>
                    <td>
                      <span className="mono" style={{ fontSize: 10.5, color: isBlocked ? 'var(--error)' : 'var(--ink-4)' }}>
                        {r.employeeUniqueId}
                      </span>
                    </td>
                    <td className="center mono">{r.earningsRows}</td>
                    <td>
                      <StatusDot status={r.earningsStatus} />
                      <span className={`badge badge-${r.earningsStatus === 'LOADED' ? 'success' : r.earningsStatus === 'BLOCKED' ? 'critical' : 'neutral'}`}>
                        {r.earningsStatus}
                      </span>
                    </td>
                    <td className="center mono">{r.deductionsRows}</td>
                    <td>
                      <StatusDot status={r.deductionsStatus} />
                      <span className={`badge badge-${r.deductionsStatus === 'LOADED' ? 'success' : r.deductionsStatus === 'BLOCKED' ? 'critical' : 'neutral'}`}>
                        {r.deductionsStatus}
                      </span>
                    </td>
                    <td>
                      {r.httpStatus
                        ? <span className={`http-${r.httpStatus}`}>{r.httpStatus}</span>
                        : <span className="http-null">—</span>}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)' }}>
                      {r.timestamp || '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Open Actions */}
      <div className="card">
        <div className="card-header">
          <h2>Open Actions Required</h2>
          <span className="badge badge-critical">1 Unresolved</span>
        </div>
        <div className="card-body">
          <div style={{
            background: 'var(--error-bg)', border: '1px solid #f5c6c6',
            borderRadius: 'var(--radius)', padding: '16px 18px',
          }}>
            <div style={{ fontWeight: 700, color: 'var(--error)', marginBottom: 6 }}>🚫 Carlos Vega — GUID Unresolved</div>
            <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.65 }}>
              2 earning rows + 1 deduction row are blocked. To resolve:<br />
              1. Call <code style={{ background: '#f5e0e0', padding: '1px 5px', borderRadius: 3, fontFamily: 'var(--font-mono)', fontSize: 12 }}>GET /api/v1/employees?clientId=ACM-2024-001</code> to retrieve EmployeeUniqueId for Carlos Vega.<br />
              2. Re-run Step 3 (Extractor V3) with updated employee_profiles.json to replace GUID_REQUIRED placeholder.<br />
              3. Re-run Steps 4 → 6 for Carlos Vega only.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
